import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, Loader2, ShieldCheck, ArrowRight } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { loginUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    const result = await loginUser(email, password);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.message);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 relative overflow-hidden bg-[#C2BDB4]">
      {/* Background decoration */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#2C4F1A] opacity-10 rounded-full blur-[100px]" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[#B8922A] opacity-10 rounded-full blur-[100px]" />

      <div className="w-full max-w-md z-10">
        <div className="bg-white/80 backdrop-blur-xl p-8 rounded-3xl shadow-2xl border border-white/20">
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-[#2C4F1A] rounded-2xl flex items-center justify-center shadow-lg shadow-green-900/20 mb-4 transition-transform hover:scale-105 duration-300">
               <ShieldCheck className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-extrabold text-gray-800 tracking-tight">HUNTER+</h1>
            <p className="text-gray-500 font-medium text-sm mt-1 uppercase letter-spacing-widest">Portal de Gestión de Talento</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 text-red-600 p-4 rounded-xl text-sm font-medium border border-red-100 animate-shake">
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-400 uppercase ml-1">Correo Electrónico</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#2C4F1A] transition-colors">
                  <Mail size={18} />
                </div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl text-gray-700 focus:bg-white focus:ring-2 focus:ring-[#2C4F1A]/20 focus:border-[#2C4F1A] transition-all outline-none text-sm shadow-sm"
                  placeholder="ejemplo@empresa.com"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-400 uppercase ml-1">Contraseña</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#2C4F1A] transition-colors">
                  <Lock size={18} />
                </div>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl text-gray-700 focus:bg-white focus:ring-2 focus:ring-[#2C4F1A]/20 focus:border-[#2C4F1A] transition-all outline-none text-sm shadow-sm"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#2C4F1A] text-white py-4 rounded-2xl font-bold text-sm shadow-lg shadow-green-900/20 hover:bg-[#1E3812] transform active:scale-[0.98] transition-all flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    INGRESAR AL SISTEMA
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-50 flex flex-col items-center gap-4 text-center">
            <p className="text-xs text-gray-400 font-medium leading-relaxed">
              Al ingresar aceptas nuestras políticas de privacidad y términos de servicio.
            </p>
            <div className="flex gap-4 text-[10px] items-center text-gray-300 font-bold tracking-widest uppercase">
               <span>V 2.0.26</span>
               <span>•</span>
               <span>HUNTER+ SaaS</span>
            </div>
          </div>
        </div>
        
        {/* Footer text */}
        <p className="text-center mt-8 text-black/40 text-xs font-bold tracking-tighter uppercase italic">
          Optimized for Talent Acquisition Performance
        </p>
      </div>
    </div>
  );
};

export default Login;
